import { AlertController, Platform } from '@ionic/angular'
import { Injectable } from '@angular/core'
import { SwUpdate, type VersionEvent } from '@angular/service-worker'

import { TranslationService } from './translation.service'
import { UsefulTools } from 'src/app/tools/useful-tools'
import { Router } from '@angular/router'
@Injectable({
  providedIn: 'root'
  })
export class SwManagerService {
  serviceWorker: ServiceWorkerRegistration
  boolNotificationsBlockedInTheBrowser = false

  strNotificationTitle: string
  strAlertBody: string
  strAlertBtnEnable: string
  strAlertBtnDisable: string

  constructor (
    private alertController: AlertController,
    public swUpdate: SwUpdate,
    private translation: TranslationService,
    private usefulTools: UsefulTools,
    private platform: Platform,
    private router: Router
  ) {
    this.loadTranslations()
    this.getServiceWorker()
  }

  /**
   * @function update
   * @description Manager the update service worker
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  update (): boolean {
    console.log('Initialize SwManagerService')
    localStorage.setItem('sw-version-detected', 'true')

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            console.log(`New version detected: ${event.version.hash}`)
            localStorage.setItem('sw-version-detected', event.version.hash)
            break
          case 'VERSION_READY':
            console.log('Current version is ready.')
            console.log(`New version: ${event.latestVersion.hash}`)
            localStorage.setItem('sw-version-ready', event.latestVersion.hash)

            this.swUpdate.activateUpdate().then(() => {
              console.log('New version activated. Reloading...')
              localStorage.setItem('sw-version-activated', 'New version installed')
              this.reloadPage()
            })
            break

          case 'VERSION_INSTALLATION_FAILED':
            console.error(`Failed to install version: ${event.version.hash}`)
            localStorage.setItem('sw-version-installation-failed', event.version.hash)
            break

          default:
            console.warn('Unhandled version update event:', event.type)
        }
      })
    } else {
      console.warn('Service Worker updates are not enabled.')
      localStorage.setItem('sw-updates-disabled', 'true')
    }

    return true
  }

  /**
   * @function reloadPage
   * @description Reloads the current document location.
   * @author Taylor Lacort
   * @param {void}
   * @returns {void}
   */
  async reloadPage () {
    if (this.platform.is('hybrid')) { await this.router.navigate(['/home']) }
    this.doReload()
  }

  /**
   * @function doReload
   * @description Wrapper to reload the current window location (extracted for testability).
   * @author Taylor Lacort
   * @param {void}
   * @returns {void}
   */
  private doReload (): void {
    window.location.reload()
  }

  /**
   * @function loadTranslations
   * @description To get translated terms
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise<boolean>}
   */
  async loadTranslations (): Promise<boolean> {
    this.strNotificationTitle = await this.translation.translate('SwManagerService.notificationTitle')
    this.strAlertBody = await this.translation.translate('SwManagerService.alertBody')
    this.strAlertBtnEnable = await this.translation.translate('SwManagerService.alertBtnEnable')
    this.strAlertBtnDisable = await this.translation.translate('SwManagerService.alertBtnDisable')
    return true
  }

  /**
   * @function getServiceWorker
   * @description To get service worker instance
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise}
   */
  async getServiceWorker (): Promise<boolean> {
    this.serviceWorker = await navigator.serviceWorker.getRegistration('ngsw-worker.js')
    return true
  }

  /**
   * @function showPushNotification
   * @description To show push notifications
   * @author Iago Rocha
   * @param {NotificationOptions} notificationOptions
   * @returns {Promise}
   */
  async showPushNotification (notificationOptions: NotificationOptions): Promise<boolean> {
    const strPermissionBrowserStatus = await this.getPermissionStatusNotificationBrowser()
    if (this.isPushNotificationsAllowed() && strPermissionBrowserStatus === 'granted' && this.serviceWorker) {
      await this.serviceWorker.showNotification(this.strNotificationTitle, notificationOptions)
      return true
    }
    return false
  }

  /**
   * @function buildNotificationOptions
   * @description To build notification options
   * @author Iago Rocha
   * @param {string} strType
   * @param {string} strMessageBody
   * @returns {Promise<NotificationOptions>}
   */
  async buildNotificationOptions (strType: string, strMessageBody: string): Promise<NotificationOptions> {
    const notificationOptions: NotificationOptions = {
      badge: 'assets/icons/icon-badge.png',
      icon: 'assets/icons/icon-512x512.png',
      requireInteraction: true
    }
    switch (strType) {
      case 'new_customer':
        notificationOptions.body = await this.translation.translate('SwManagerService.notificationNewCustomer', {
          queueLabel: strMessageBody
        })
    }
    return notificationOptions
  }

  /**
   * @function checkNotificationPermission
   * @description Check permission to notification
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise<void>}
   */
  async checkNotificationPermission (): Promise<void> {
    // Check if is not Chrome Browser
    if ((this.usefulTools.getBrowserName() !== 'Chrome') && (!this.platform.is('pwa'))) {
      this.blockPushNotifications()
      return
    }
    // Show push notifications subscription if not already prompted
    if (!this.isPushNotificationsAlreadyPrompted()) {
      this.showPushNotificationsSubscription()
      return
    }
    // Check if permission notification browser is denied
    if (await this.getPermissionStatusNotificationBrowser() === 'denied') {
      this.blockPushNotifications()
    }
  }

  /**
   * @function blockPushNotifications
   * @description Block and disable push notifications
   * @author Iago Rocha
   * @param {void}
   * @returns {void}
   */
  blockPushNotifications (): void {
    this.boolNotificationsBlockedInTheBrowser = true
    this.disablePushNotifications()
  }

  /**
   * @function enablePushNotifications
   * @description To enable notifications
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise}
   */
  async enablePushNotifications (): Promise<string> {
    const strStatePermission = await this.getPermissionStatusNotificationBrowser()
    switch (strStatePermission) {
      case 'denied':
        this.blockPushNotifications()
        break
      case 'prompt':
        this.showPushNotificationsSubscription()
        break
      case 'granted':
        this.saveNotificationPermission(true)
        break
    }
    return strStatePermission
  }

  /**
   * @function disablePushNotifications
   * @description To disable notifications
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  disablePushNotifications (): boolean {
    return this.saveNotificationPermission(false)
  }

  /**
   * @function showPromptNotificationsBrowser
   * @description Show prompt to allow notifications on browser
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise<string>}
   */
  async showPromptNotificationsBrowser (): Promise<string> {
    await Notification.requestPermission()
    const strStatePermission = await this.enablePushNotifications()
    return strStatePermission
  }

  /**
   * @function changeIonTogglePushNotificationsSubscription
   * @description Enable or disable the notification
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise<void>}
   */
  async changeIonTogglePushNotificationsSubscription (): Promise<void> {
    // Check if is push notification allowed from localStorage
    if (this.isPushNotificationsAllowed()) {
      this.disablePushNotifications()
    } else {
      await this.enablePushNotifications()
    }
  }

  /**
   * @function setCheckedIonTogglePushNotification
   * @description Enable or disable attribute checked for element ion-toggle
   * @author Iago Rocha
   * @param {boolean} boolChecked
   * @returns {void}
   */
  setCheckedIonTogglePushNotification (boolChecked: boolean): void {
    try {
      const ionTogglePushNotification = document.getElementById('togglePushNotification')
      if (ionTogglePushNotification) {
        ionTogglePushNotification.setAttribute('checked', String(boolChecked))
      }
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * @function saveNotificationPermission
   * @description Save notification permission in localStorage
   * @author Iago Rocha
   * @param {boolean} boolState
   * @returns {boolean}
   */
  saveNotificationPermission (boolState: boolean): boolean {
    localStorage.setItem('pushNotification', JSON.stringify(boolState))
    this.setCheckedIonTogglePushNotification(boolState)
    return boolState
  }

  /**
   * @function isPushNotificationsAllowed
   * @description To get notification permission from local storage
   * @author Iago Rocha
   * @param {void}
   * @returns {any}
   */
  isPushNotificationsAllowed (): any {
    return JSON.parse(localStorage.getItem('pushNotification'))
  }

  /**
   * @function isPushNotificationsAlreadyPrompted
   * @description Check if permission to notifications already prompted
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  isPushNotificationsAlreadyPrompted (): boolean {
    if (this.isPushNotificationsAllowed() === null) {
      return false
    } else {
      return true
    }
  }

  /**
   * @function getPermissionStatusNotificationBrowser
   * @description Get permission status notification browser
   * @author Iago Rocha
   * @param {void}
   * @returns {string}
   */
  async getPermissionStatusNotificationBrowser (): Promise<string> {
    const permissionStatus = await navigator.permissions.query({
      name: 'notifications'
    })
    return permissionStatus.state
  }

  /**
   * @function showPushNotificationsSubscription
   * @description Show alert asking for permission to enable notifications
   * @author Iago Rocha
   * @param {void}
   * @returns {Promise<boolean>}
   */
  async showPushNotificationsSubscription (): Promise<boolean> {
    try {
      const alert = await this.alertController.create({
        header: this.strNotificationTitle,
        message: this.strAlertBody,
        backdropDismiss: false,
        buttons: [
          {
            text: this.strAlertBtnDisable,
            role: 'cancel',
            cssClass: 'alertNotifications',
            handler: () => this.disablePushNotifications()
          },
          {
            text: this.strAlertBtnEnable,
            handler: () => this.showPromptNotificationsBrowser()
          }
        ]
      })
      await alert.present()
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }
}
